

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { Form, Input, Button, Card, Typography, Alert, Row, Col, Divider, message, Modal } from "antd";
import {
  UserOutlined,
  LockOutlined,
  LoadingOutlined,
  BookOutlined,
  TeamOutlined,
  TrophyOutlined,
  RocketOutlined,
  StarOutlined,
  BulbOutlined,
  MailOutlined,
  CheckCircleOutlined
} from "@ant-design/icons";

import { login } from "../actions/auth";
import GoogleLoginButton from "./google/GoogleLogin";
import AuthService from "../services/auth.service";

const { Title, Paragraph, Text } = Typography;

/* ============ Couleurs Nomidya ============ */
const C = {
  blue: "#0054A6",
  blueDark: "#003B75",
  orange: "#E8860C",
  orangeLight: "#FF8C00",
  orangeBg: "#fff8f0",
  blueLight: "#e8f1fb",
  text: "#333",
  textLight: "#888",
  border: "#eaedf2",
  bg: "#f7f8fa",
};

const Login = (props) => {
  let navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // ============ ÉTATS MOT DE PASSE OUBLIÉ ============
  const [forgotModalVisible, setForgotModalVisible] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotUsername, setForgotUsername] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  const { isLoggedIn } = useSelector((state) => state.auth);
  const { message: errorMessage } = useSelector((state) => state.message);

  const dispatch = useDispatch();

  const handleLogin = () => {
    setLoading(true);
    dispatch(login(username, password))
      .then(() => {
        navigate("/home");
        window.location.reload();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleGoogleSuccess = async (googleResponse) => {
    setGoogleLoading(true);
    try {
      const response = await AuthService.googleLogin(googleResponse);
      if (response.accessToken) {
        localStorage.setItem("user", JSON.stringify(response));
        message.success("Connexion Google réussie !");
        navigate("/home");
        window.location.reload();
      }
    } catch (error) {
      console.error('Erreur Google Login:', error);
      if (error.response && error.response.status === 404) {
        try {
          await AuthService.googleSignup(googleResponse);
          message.success("Inscription Google réussie ! Connexion en cours...");
          const loginResponse = await AuthService.googleLogin(googleResponse);
          if (loginResponse.accessToken) {
            localStorage.setItem("user", JSON.stringify(loginResponse));
            navigate("/home");
            window.location.reload();
          }
        } catch (signupError) {
          console.error('Erreur Google Signup:', signupError);
          message.error(signupError.response?.data?.message || "Erreur lors de l'inscription avec Google");
        }
      } else {
        message.error(error.response?.data?.message || "Erreur lors de la connexion avec Google");
      }
      setGoogleLoading(false);
    }
  };

  const handleGoogleFailure = (error) => {
    console.error('Google Login Failed:', error);
    message.error("Échec de la connexion Google");
    setGoogleLoading(false);
  };

  // ============ HANDLER MOT DE PASSE OUBLIÉ ============
  const handleForgotPassword = async () => {
    if (!forgotUsername.trim()) {
      message.warning("Veuillez entrer votre nom d'utilisateur.");
      return;
    }

    if (!forgotEmail.trim()) {
      message.warning("Veuillez entrer votre adresse email.");
      return;
    }

    // Validation email basique
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotEmail)) {
      message.warning("Veuillez entrer une adresse email valide.");
      return;
    }

    setForgotLoading(true);
    try {
      await AuthService.forgotPassword(forgotEmail, forgotUsername);
      setForgotSuccess(true);
    } catch (error) {
      const msg = error.response?.data?.message || "Erreur lors de l'envoi de l'email.";
      message.error(msg);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleCloseForgotModal = () => {
    setForgotModalVisible(false);
    setForgotEmail("");
    setForgotUsername("");
    setForgotSuccess(false);
    setForgotLoading(false);
  };

  if (isLoggedIn) {
    return <Navigate to="/home" />;
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(135deg, ${C.blueDark} 0%, ${C.blue} 50%, ${C.blueDark} 100%)`,
      display: "flex",
      alignItems: "center",
      padding: "20px 0",
    }}>
      <div style={{ width: "100%", maxWidth: 1200, margin: "0 auto", padding: "0 20px" }}>
        <Row gutter={[40, 40]} align="middle">

          {/* ============ Section Description ============ */}
          <Col xs={24} lg={12}>
            <div style={{
              background: "rgba(255, 255, 255, 0.97)",
              borderRadius: 16,
              padding: "36px",
              boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
            }}>
              {/* Logo + titre */}
              <div style={{ textAlign: "center", marginBottom: 28 }}>
                <div style={{
                  background: `linear-gradient(135deg, ${C.blue}, ${C.blueDark})`,
                  borderRadius: "50%",
                  width: 70, height: 70,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 16px",
                  boxShadow: `0 8px 24px ${C.blue}33`,
                }}>
                  <RocketOutlined style={{ fontSize: 28, color: "white" }} />
                </div>
                <Title level={1} style={{
                  color: C.blueDark,
                  marginBottom: 6,
                  fontSize: "2.2em",
                  fontWeight: 800,
                }}>
                  N<span style={{ color: C.orange }}>O</span>MIDYA
                </Title>
                <Text style={{ fontSize: 16, color: C.textLight }}>
                  Plateforme d'apprentissage innovante
                </Text>
              </div>

              {/* Équipe */}
              <div style={{ marginBottom: 24 }}>
                <Title level={3} style={{ color: C.blueDark, marginBottom: 12, fontSize: 20 }}>
                  <TeamOutlined style={{ color: C.blue, marginRight: 8 }} />
                  Une Équipe Unie
                </Title>
                <Paragraph style={{ color: C.textLight, fontSize: 14, lineHeight: 1.7 }}>
                  Chez Nomidya, nous sommes une équipe unie par la passion de l'éducation et la volonté
                  de faire une différence dans la vie de nos apprenants. Ensemble, nous formons une
                  communauté dynamique, ouverte et engagée.
                </Paragraph>
              </div>

              {/* Caractéristiques */}
              <Row gutter={[16, 16]}>
                {[
                  { icon: <BookOutlined />, color: C.blue, title: "Apprentissage Personnalisé", desc: "Contenus adaptés à votre rythme" },
                  { icon: <TrophyOutlined />, color: C.orange, title: "Suivi des Progrès", desc: "Mesurez vos performances" },
                  { icon: <StarOutlined />, color: "#00875A", title: "Certification", desc: "Validez vos compétences" },
                  { icon: <BulbOutlined />, color: C.blue, title: "Innovation", desc: "Technologies de pointe" },
                ].map((item, i) => (
                  <Col span={12} key={i}>
                    <div style={{
                      textAlign: "center", padding: 16, borderRadius: 10,
                      border: `1px solid ${C.border}`, background: "white",
                    }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: 10, margin: "0 auto 8px",
                        background: item.color, display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 18, color: "white",
                      }}>
                        {item.icon}
                      </div>
                      <div style={{ color: C.blueDark, fontWeight: 700, fontSize: 13 }}>{item.title}</div>
                      <div style={{ color: C.textLight, fontSize: 11 }}>{item.desc}</div>
                    </div>
                  </Col>
                ))}
              </Row>

              {/* Citation */}
              <div style={{
                background: `linear-gradient(135deg, ${C.blue}, ${C.blueDark})`,
                borderRadius: 12,
                padding: 18,
                textAlign: "center",
                marginTop: 24,
              }}>
                <Text style={{ color: "rgba(255,255,255,0.9)", fontStyle: "italic", fontSize: 13, lineHeight: 1.6 }}>
                  "Ensemble, nous construisons un avenir où l'apprentissage transforme des vies
                  et où les compétences ouvrent des portes vers de nouvelles opportunités."
                </Text>
              </div>
            </div>
          </Col>

          {/* ============ Section Connexion ============ */}
          <Col xs={24} lg={12}>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Card
                style={{
                  width: "100%", maxWidth: 400,
                  background: "rgba(255, 255, 255, 0.97)",
                  borderRadius: 16,
                  boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
                  border: "none",
                }}
                bodyStyle={{ padding: 36 }}
              >
                {/* Header */}
                <div style={{ textAlign: "center", marginBottom: 28 }}>
                  <img src="/img/nomidya.png" alt="Nomidya"
                    style={{ width: 110, marginBottom: 16 }} />
                  <Title level={2} style={{ color: C.blueDark, marginBottom: 8, fontSize: 22, fontWeight: 800 }}>
                    Bienvenue sur <span style={{ color: C.orange }}>Nomidya</span>
                  </Title>
                  <Text style={{ color: C.textLight, fontSize: 13 }}>
                    Connectez-vous pour accéder à votre espace d'apprentissage
                  </Text>
                </div>

                {/* Google */}
                <GoogleLoginButton
                  onSuccess={handleGoogleSuccess}
                  onFailure={handleGoogleFailure}
                  loading={googleLoading}
                />

                <Divider style={{ margin: "20px 0" }}>
                  <Text style={{ color: "#ccc", fontSize: 12 }}>OU</Text>
                </Divider>

                {/* Form */}
                <Form name="login" layout="vertical" onFinish={handleLogin} style={{ width: "100%" }}>
                  <Form.Item name="username"
                    rules={[{ required: true, message: "Veuillez entrer votre nom d'utilisateur !" }]}>
                    <Input
                      prefix={<UserOutlined style={{ color: C.blue, fontSize: 15, marginRight: 6 }} />}
                      placeholder="Nom d'utilisateur"
                      size="large"
                      onChange={(e) => setUsername(e.target.value)}
                      style={{
                        borderRadius: 10,
                        border: `1.5px solid ${C.border}`,
                        padding: "10px 12px",
                      }}
                    />
                  </Form.Item>

                  <Form.Item name="password"
                    rules={[{ required: true, message: "Veuillez entrer votre mot de passe !" }]}>
                    <Input.Password
                      prefix={<LockOutlined style={{ color: C.blue, fontSize: 15, marginRight: 6 }} />}
                      placeholder="Mot de passe"
                      size="large"
                      onChange={(e) => setPassword(e.target.value)}
                      style={{
                        borderRadius: 10,
                        border: `1.5px solid ${C.border}`,
                        padding: "10px 12px",
                      }}
                    />
                  </Form.Item>

                  {/* 🆕 LIEN MOT DE PASSE OUBLIÉ */}
                  <div style={{ textAlign: "right", marginTop: -12, marginBottom: 16 }}>
                    <Button
                      type="link"
                      onClick={() => setForgotModalVisible(true)}
                      style={{
                        padding: 0,
                        fontSize: 13,
                        color: C.blue,
                        fontWeight: 500,
                      }}
                    >
                      Mot de passe oublié ?
                    </Button>
                  </div>

                  <Form.Item style={{ marginBottom: 16 }}>
                    <Button
                      type="primary" htmlType="submit" block size="large"
                      loading={loading}
                      icon={loading && <LoadingOutlined />}
                      style={{
                        background: `linear-gradient(135deg, ${C.blue}, ${C.blueDark})`,
                        border: "none",
                        borderRadius: 10,
                        height: 48,
                        fontSize: 15,
                        fontWeight: 700,
                        boxShadow: `0 4px 16px ${C.blue}33`,
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={e => { e.target.style.transform = "translateY(-2px)"; e.target.style.boxShadow = `0 6px 20px ${C.blue}44`; }}
                      onMouseLeave={e => { e.target.style.transform = "translateY(0)"; e.target.style.boxShadow = `0 4px 16px ${C.blue}33`; }}
                    >
                      Connexion
                    </Button>
                  </Form.Item>
                </Form>

                {errorMessage && (
                  <Alert
                    message="Erreur de connexion"
                    description={errorMessage}
                    type="error" showIcon
                    style={{ marginTop: 16, borderRadius: 10, border: "none" }}
                  />
                )}

                {/* ⭐ NOUVEAU : Lien "Pas encore de compte ?" */}
                <div style={{
                  textAlign: "center",
                  marginTop: 20,
                  padding: "16px 0",
                  borderTop: `1px solid ${C.border}`,
                }}>
                  <Text style={{ color: C.textLight, fontSize: 13 }}>
                    Pas encore de compte ?{" "}
                  </Text>
                  <Button
                    type="link"
                    onClick={() => navigate("/register")}
                    style={{
                      padding: 0,
                      fontSize: 13,
                      color: C.orange,
                      fontWeight: 700,
                    }}
                  >
                    Créer un compte gratuitement
                  </Button>
                </div>

                <div style={{ textAlign: "center", marginTop: 16 }}>
                  <Text style={{ color: "#ccc", fontSize: 11 }}>
                    © 2026 Nomidya — La Carrière. Tous droits réservés.
                  </Text>
                </div>
              </Card>
            </div>
          </Col>
        </Row>
      </div>

      {/* ============ 🆕 MODAL MOT DE PASSE OUBLIÉ ============ */}
      <Modal
        open={forgotModalVisible}
        onCancel={handleCloseForgotModal}
        footer={null}
        centered
        width={440}
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: 16, overflow: "hidden" }}
      >
        <div style={{ padding: "32px 28px" }}>
          {!forgotSuccess ? (
            <>
              {/* Header */}
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <div style={{
                  width: 60, height: 60, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${C.blue}, ${C.blueDark})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 16px",
                  boxShadow: `0 6px 20px ${C.blue}33`,
                }}>
                  <LockOutlined style={{ fontSize: 24, color: "white" }} />
                </div>
                <Title level={3} style={{ color: C.blueDark, marginBottom: 6, fontSize: 20, fontWeight: 700 }}>
                  Mot de passe oublié ?
                </Title>
                <Text style={{ color: C.textLight, fontSize: 13, lineHeight: 1.6 }}>
                  Entrez votre nom d'utilisateur et votre adresse email pour recevoir un lien de réinitialisation.
                </Text>
              </div>

              {/* Champ nom d'utilisateur */}
              <div style={{ marginBottom: 14 }}>
                <Input
                  prefix={<UserOutlined style={{ color: C.blue, fontSize: 15, marginRight: 6 }} />}
                  placeholder="Votre nom d'utilisateur"
                  size="large"
                  value={forgotUsername}
                  onChange={(e) => setForgotUsername(e.target.value)}
                  style={{
                    borderRadius: 10,
                    border: `1.5px solid ${C.border}`,
                    padding: "10px 12px",
                  }}
                />
              </div>

              {/* Champ email */}
              <div style={{ marginBottom: 20 }}>
                <Input
                  prefix={<MailOutlined style={{ color: C.blue, fontSize: 15, marginRight: 6 }} />}
                  placeholder="Votre adresse email"
                  size="large"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  onPressEnter={handleForgotPassword}
                  style={{
                    borderRadius: 10,
                    border: `1.5px solid ${C.border}`,
                    padding: "10px 12px",
                  }}
                />
              </div>

              {/* Bouton envoyer */}
              <Button
                type="primary" block size="large"
                loading={forgotLoading}
                onClick={handleForgotPassword}
                style={{
                  background: `linear-gradient(135deg, ${C.orange}, ${C.orangeLight})`,
                  border: "none",
                  borderRadius: 10,
                  height: 46,
                  fontSize: 14,
                  fontWeight: 700,
                  boxShadow: `0 4px 16px ${C.orange}33`,
                }}
              >
                {forgotLoading ? "Envoi en cours..." : "Envoyer le lien de réinitialisation"}
              </Button>

              {/* Retour */}
              <div style={{ textAlign: "center", marginTop: 16 }}>
                <Button
                  type="link"
                  onClick={handleCloseForgotModal}
                  style={{ color: C.textLight, fontSize: 13 }}
                >
                  ← Retour à la connexion
                </Button>
              </div>
            </>
          ) : (
            /* ============ ÉCRAN DE SUCCÈS ============ */
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{
                width: 70, height: 70, borderRadius: "50%",
                background: "linear-gradient(135deg, #52c41a, #389e0d)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 20px",
                boxShadow: "0 6px 20px rgba(82, 196, 26, 0.3)",
              }}>
                <CheckCircleOutlined style={{ fontSize: 32, color: "white" }} />
              </div>
              <Title level={3} style={{ color: "#389e0d", marginBottom: 8, fontSize: 20, fontWeight: 700 }}>
                Email envoyé !
              </Title>
              <Text style={{ color: C.textLight, fontSize: 13, lineHeight: 1.7, display: "block", marginBottom: 8 }}>
                Un lien de réinitialisation a été envoyé à :
              </Text>
              <Text strong style={{ color: C.blueDark, fontSize: 15, display: "block", marginBottom: 16 }}>
                {forgotEmail}
              </Text>
              <div style={{
                background: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: 10,
                padding: "12px 16px",
                marginBottom: 20,
              }}>
                <Text style={{ color: "#389e0d", fontSize: 12, lineHeight: 1.6 }}>
                  📧 Vérifiez votre boîte de réception et vos spams. Le lien est valable pendant <strong>30 minutes</strong>.
                </Text>
              </div>
              <Button
                type="primary" block size="large"
                onClick={handleCloseForgotModal}
                style={{
                  background: `linear-gradient(135deg, ${C.blue}, ${C.blueDark})`,
                  border: "none",
                  borderRadius: 10,
                  height: 44,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Retour à la connexion
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Login;